/* libs */
import React, { Fragment, Component } from "react";
import PropTypes from "prop-types";
import { asField } from "informed";
import {
	Editor,
	EditorState,
	AtomicBlockUtils,
	CompositeDecorator,
	SelectionState,
	Modifier,
	getDefaultKeyBinding,
	DefaultDraftBlockRenderMap,
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import Immutable from "immutable";

import ExtendedRichUtils from "./TextEditorComponents/ExtendedRichUtils";
import BlockSelector from "./TextEditorComponents/BlockSelector";
import ValidateItem from "../../../helpers/validation/validateInput";
import Switcher from "../../ui/Switcher";
import parseAppKey from "../../../helpers/routeHelpers/parseAppKey";
import parseExperienceId from "../../../helpers/routeHelpers/parseExperienceId";
import GetItem from "../../../helpers/dataProviders/getItem";
import Loader from "../../ui/Loader";
import validatehoc from "../Validator";
import FileManagerUI from "../../ui/FileManager";

const exportOptions = {
	blockRenderers: {
		small: block => {
			if (block.getData().get("textAlignment")) {
				return `<verrissmall style="text-align: ${block
					.getData()
					.get("textAlignment")
					.toLowerCase()}" textAlignment="${block
					.getData()
					.get("textAlignment")
					.toLowerCase()}">${block.getText()}</verrissmall>`;
			} else {
				return `<verrissmall>${block.getText()}</verrissmall>`;
			}
		},
		mini: block => {
			if (block.getData().get("textAlignment")) {
				return `<verrismini style="text-align: ${block
					.getData()
					.get("textAlignment")
					.toLowerCase()}" textAlignment="${block
					.getData()
					.get("textAlignment")
					.toLowerCase()}">${block.getText()}</verrismini>`;
			} else {
				return `<verrismini>${block.getText()}</verrismini>`;
			}
		},
	},
	blockStyleFn: block => {
		let output = {};
		if (block.getData().get("textAlignment")) {
			output = Object.assign({}, output, {
				style: {
					textAlign: block
						.getData()
						.get("textAlignment")
						.toLowerCase(),
				},
				attributes: {
					textAlignment: block.getData().get("textAlignment"),
				},
			});
		}

		return output;
	},
};

const importOptions = {
	blockTypes: {
		verrissmall: "small",
		verrismini: "mini",
	},
	customBlockFn: element => {
		if (element.getAttribute("textAlignment")) {
			return { data: { textAlignment: element.getAttribute("textAlignment").toUpperCase() } };
		}
	},
};

class TextInput extends Component {
	constructor(props) {
		super(props);

		let initialContent = this.importHtml();

		this.state = {
			minimizePlaceholder: false,
			editorState: initialContent,
			showURLInput: false,
			showImageInput: false,
			urlValue: "",
			emailValue: "",
			experienceValue: "",
			pageValue: "",
			imageValue: "",
			urlType: "external",
			experienceOptions: [],
			pageOptions: [],
			loadingPages: false,
		};
		this.onURLChange = e => this.setState({ urlValue: e.target.value });
		this.onEmailChange = e => this.setState({ emailValue: e.target.value });
		this.onImageChange = url => this.setState({ imageValue: url });
		this.editor = React.createRef();
		this.linkInput = React.createRef();
		this.emailInput = React.createRef();
		this.appKey = parseAppKey();
		this.onChange = editorState => {
			this.setState({ editorState }, () => {
				this.exportHtml();
			});
		};
	}

	exportHtml = () => {
		const { fieldApi } = this.props;
		const { editorState } = this.state;
		let html = stateToHTML(editorState.getCurrentContent(), exportOptions);
		fieldApi.setValue(html);
		this.props.fieldApi.setTouched(true);
	};

	importHtml = () => {
		const { fieldApi } = this.props;
		let html = fieldApi.getValue();
		const decorator = new CompositeDecorator([
			{
				strategy: findLinkEntities,
				component: Link,
			},
		]);
		let newEditorState = {};
		if (html) {
			var newState = stateFromHTML(html, importOptions);
			newEditorState = EditorState.createWithContent(newState, decorator);
		} else {
			newEditorState = EditorState.createEmpty(decorator);
		}
		return newEditorState;
	};

	componentDidMount() {
		GetItem(`/apps/${this.appKey}/experiences`).then(data => {
			let experienceOptions = [];
			data.data.forEach(experience => {
				experienceOptions.push({ key: experience.id, value: experience.id, label: experience.name });
			});
			this.setState({ experienceOptions });
		});
	}

	validate = value => {
		const { placeholder, isRequired, validationRegex, minLength, maxLength, regexMessage } = this.props;
		return ValidateItem(value, placeholder, isRequired, validationRegex, minLength, maxLength, regexMessage);
	};

	onFocus = () => {
		this.setState({
			minimizePlaceholder: true,
		});
	};

	onBlur = value => {
		if (!value || value.length < 1) {
			this.setState({
				minimizePlaceholder: false,
			});
		}
	};

	focusEditor = () => {
		this.editor.current.focus();
	};

	handleReturn = (e, editorState) => {
		const contentState = editorState.getCurrentContent();
		const selection = editorState.getSelection();
		const startKey = selection.getStartKey();
		const block = contentState.getBlockForKey(startKey);
		if (block.getType() === "atomic") {
			return "handled";
		}
	};

	handleKeyCommand(command, editorState) {
		const newState = ExtendedRichUtils.handleKeyCommand(editorState, command);
		if (newState) {
			this.onChange(newState);
			return "handled";
		}
		return "not-handled";
	}

	handleBeforeInput = (chars, editorState) => {
		const contentState = editorState.getCurrentContent();
		const selection = editorState.getSelection();
		const startKey = selection.getStartKey();
		const block = contentState.getBlockForKey(startKey);
		if (block.getType() === "atomic") {
			return "handled";
		}
	};

	handleTab = e => {
		const { editorState } = this.state;
		if (e.keyCode === 9) {
			const newEditorState = ExtendedRichUtils.onTab(e, editorState, 6 /* maxDepth */);
			if (newEditorState !== editorState) {
				this.onChange(newEditorState);
			}
			return;
		}

		return getDefaultKeyBinding(e);
	};

	toggleBlockType = blockType => {
		this.onChange(ExtendedRichUtils.toggleBlockType(this.state.editorState, blockType));
		setTimeout(() => this.focusEditor(), 0);
	};

	setInlineStyle = style => {
		this.onChange(ExtendedRichUtils.toggleInlineStyle(this.state.editorState, style));
		setTimeout(() => this.focusEditor(), 0);
	};

	setAlignment = alignment => {
		this.onChange(ExtendedRichUtils.toggleAlignment(this.state.editorState, alignment));
		setTimeout(() => this.focusEditor(), 0);
	};

	promptForLink = e => {
		e.preventDefault();
		const { editorState } = this.state;
		const selection = editorState.getSelection();
		if (!selection.isCollapsed()) {
			const contentState = editorState.getCurrentContent();
			const startKey = editorState.getSelection().getStartKey();
			const startOffset = editorState.getSelection().getStartOffset();
			const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
			const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
			let urlValue = "";
			let emailValue = "";
			let experienceValue = "";
			let pageValue = "";
			let urlType = "external";
			let fetchPages = false;
			if (linkKey) {
				const linkInstance = contentState.getEntity(linkKey);
				let url = linkInstance.getData().url;
				urlValue = url;
				if (url.startsWith("mailto:")) {
					urlType = "email";
					urlValue = "";
					emailValue = url.split(":")[1];
				} else if (url.startsWith("verris-internal://")) {
					urlType = "internal";
					urlValue = "";
					let data = url.split("://")[1];
					let obj = JSON.parse(atob(data));
					if (obj.experienceId) {
						experienceValue = obj.experienceId;
					}
					if (obj.pageId) {
						pageValue = obj.pageId;
					}
				}
			} else if (parseExperienceId()) {
				experienceValue = parseExperienceId();
				fetchPages = true;
			}
			this.setState(
				{
					showURLInput: true,
					urlValue,
					urlType,
					emailValue,
					experienceValue,
					pageValue,
				},
				() => {
					switch (urlType) {
						case "external":
							setTimeout(() => this.linkInput.current.focus(), 0);
							break;
						case "email":
							setTimeout(() => this.emailInput.current.focus(), 0);
							break;
						case "internal":
							this.getPages(experienceValue);
							break;
						default:
							break;
					}
					if (fetchPages) {
						this.getPages(experienceValue);
					}
				}
			);
		}
	};
	confirmLink = e => {
		e.preventDefault();
		const { editorState, urlValue, emailValue, pageValue, experienceValue, urlType } = this.state;
		const contentState = editorState.getCurrentContent();
		let url = "";
		let proceed = true;
		switch (urlType) {
			case "external":
				url = urlValue;
				break;
			case "email":
				url = `mailto:${emailValue}`;
				break;
			case "internal":
				var obj = { experienceId: experienceValue };
				if (experienceValue === "") {
					proceed = false;
				} else if (pageValue !== "") {
					obj = {
						experienceId: experienceValue,
						pageId: pageValue,
					};
				}
				var data = btoa(JSON.stringify(obj));
				url = `verris-internal://${data}`;
				break;
			default:
				break;
		}
		if (proceed) {
			const contentStateWithEntity = contentState.createEntity("LINK", "MUTABLE", { url });
			const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
			const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });
			this.setState(
				{
					editorState: ExtendedRichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey),
					showURLInput: false,
					urlValue: "",
				},
				() => {
					setTimeout(() => this.focusEditor(), 0);
				}
			);
		}
	};
	onLinkInputKeyDown = e => {
		if (e.which === 13) {
			this.confirmLink(e);
		}
	};
	removeLink = e => {
		e.preventDefault();
		const { editorState } = this.state;
		const selection = editorState.getSelection();
		if (!selection.isCollapsed()) {
			this.setState({
				editorState: ExtendedRichUtils.toggleLink(editorState, selection, null),
			});
		}
	};
	closeLinkInput = () => {
		this.setState({
			showURLInput: false,
			urlValue: "",
		});
	};

	changeUrlType = (index, option) => {
		this.setState({ urlType: option.value });
	};

	onExpChange = e => {
		let value = e.target.value;
		this.setState(
			{
				pageOptions: [],
				loadingPages: true,
				experienceValue: value,
			},
			() => {
				this.getPages(value);
			}
		);
	};

	getPages = value => {
		GetItem(`/experiences/${value}/pages`).then(data => {
			let pageOptions = [];
			data.data.forEach(page => {
				pageOptions.push({ key: page.id, value: page.id, label: page.name });
			});
			this.setState({ pageOptions, loadingPages: false });
		});
	};

	onPageChange = e => {
		let value = e.target.value;
		this.setState({
			pageValue: value,
		});
	};

	renderLinkInput = () => {
		const switcherOptions = [
			{ label: "External", value: "external" },
			{ label: "Internal", value: "internal" },
			{ label: "Email", value: "email" },
		];
		return (
			<div className="relative">
				<div className={"coverall on"} onClick={e => this.closeLinkInput(e)}></div>
				<div className="input-modal">
					<Switcher
						initialValue={this.state.urlType}
						options={switcherOptions}
						onSelect={this.changeUrlType}
					/>
					<div className="switcher-well">
						{this.state.urlType === "external" && (
							<div className="input-border">
								<input
									onChange={this.onURLChange}
									ref={this.linkInput}
									type="text"
									value={this.state.urlValue}
									onKeyDown={this.onLinkInputKeyDown}
									placeholder="URL"
								/>
							</div>
						)}
						{this.state.urlType === "email" && (
							<div className="input-border">
								<input
									onChange={this.onEmailChange}
									ref={this.emailInput}
									type="text"
									value={this.state.emailValue}
									onKeyDown={this.onLinkInputKeyDown}
									placeholder="Email Address"
								/>
							</div>
						)}
						{this.state.urlType === "internal" && (
							<div>
								<div className="input-border">
									<div className="input-icon">
										<i className="fal fa-chevron-down"></i>
									</div>
									<select onChange={this.onExpChange} value={this.state.experienceValue}>
										<option value="" disabled={true}>
											Pick an Experience
										</option>
										{this.state.experienceOptions &&
											this.state.experienceOptions.length > 0 &&
											this.state.experienceOptions.map(value => (
												<option key={value.key} value={value.value}>
													{value.label}
												</option>
											))}
									</select>
								</div>
								{this.state.loadingPages && <Loader />}
								{this.state.pageOptions.length > 0 && !this.state.loadingPages ? (
									<div className="input-border m-top">
										<div className="input-icon">
											<i className="fal fa-chevron-down"></i>
										</div>
										<select onChange={this.onPageChange} value={this.state.pageValue}>
											<option value="">Default Page</option>
											{this.state.pageOptions &&
												this.state.pageOptions.length > 0 &&
												this.state.pageOptions.map(value => (
													<option key={value.key} value={value.value}>
														{value.label}
													</option>
												))}
										</select>
									</div>
								) : (
									!this.state.loadingPages && <div className="note">No Pages To Display</div>
								)}
							</div>
						)}
					</div>

					<div className="btn btn-primary wide" onMouseDown={this.confirmLink}>
						Add Link
					</div>
				</div>
			</div>
		);
	};

	promptForImage = () => {
		this.setState({
			showImageInput: true,
			imageValue: "",
		});
	};
	closeImageInput = () => {
		this.setState({
			showImageInput: false,
			imageValue: "",
		});
	};
	confirmImage = e => {
		e.preventDefault();
		const { editorState, imageValue } = this.state;
		const contentState = editorState.getCurrentContent();
		const contentStateWithEntity = contentState.createEntity("IMAGE", "IMMUTABLE", {
			src: imageValue,
			isNew: true,
		});
		const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
		const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });

		const tempState = AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " ");

		const new_content_state = tempState.getCurrentContent();
		const block_map = new_content_state.getBlockMap();
		// eslint-disable-next-line array-callback-return
		const current_atomic_block = block_map.find(block => {
			if (block.getEntityAt(0) === entityKey) {
				return block;
			}
		});
		const atomic_block_key = current_atomic_block.getKey();
		const block_before = new_content_state.getBlockBefore(atomic_block_key).getKey();
		// eslint-disable-next-line array-callback-return
		const new_block_map = block_map.filter(block => {
			if (block.getKey() !== block_before) {
				return block;
			}
		});
		const newContentState = contentState.set("blockMap", new_block_map);
		const modifiedEditorState = EditorState.createWithContent(newContentState);

		this.setState(
			{
				editorState: modifiedEditorState,
				showImageInput: false,
				imageValue: "",
			},
			() => {
				setTimeout(() => {
					this.focusEditor();
				}, 10);
			}
		);
	};

	onImageInputKeyDown = e => {
		if (e.which === 13) {
			this.confirmLink(e);
		}
	};

	imageUploadError = error => {
		//TODO handle this error better
		console.log(error);
	};

	renderImageInput = () => {
		return (
			<div className="relative small-file-picker">
				<div className="coverall on" onClick={e => this.closeImageInput(e)}></div>
				<div className="input-modal">
					<div className="row">
						<div className="amodal-body col-sm-12">
							<FileManagerUI
								value={this.state.imageValue}
								uploadSuccess={this.onImageChange}
								uploadError={this.imageUploadError}
								removeFile={() => this.setState({ imageValue: null })}
							/>
						</div>
						<div className="amodal-footer col-sm-12">
							<div className="btn btn-primary wide" onMouseDown={this.confirmImage}>
								Confirm
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};

	blockRenderMap = Immutable.Map({
		"header-one": {
			element: "h1",
		},
		"header-two": {
			element: "h2",
		},
		"header-three": {
			element: "h3",
		},
		"header-four": {
			element: "h4",
		},
		"header-five": {
			element: "h5",
		},
		"header-six": {
			element: "h6",
		},
		small: {
			element: "small",
		},
		mini: {
			element: "mini",
		},
		unstyled: {
			element: "p",
		},
	});

	blockRender = block => {
		if (block.getType() === "atomic") {
			let entityKey = block.getEntityAt(0);
			let i = 1;
			let len = 10; //This is just a dumb way to find the entityKey because we removed the original one
			while (!entityKey && i < len) {
				entityKey = block.getEntityAt(i);
				i++;
			}
			if (entityKey) {
				const entity = this.state.editorState.getCurrentContent().getEntity(entityKey);
				const type = entity.getType();
				if (type === "IMAGE") {
					return {
						component: this.Image,
						editable: true,
					};
				}
			}
		} else if (block.getType() === "small") {
			return {
				element: "small",
			};
		} else if (block.getType() === "mini") {
			return {
				element: "mini",
			};
		}
	};

	blockStyleFn = contentBlock => {
		const textAlignStyle = contentBlock.getData().get("textAlignment");
		switch (textAlignStyle) {
			case "RIGHT":
				return "align-right";
			case "CENTER":
				return "align-center";
			case "LEFT":
				return "align-left";
			case "JUSTIFY":
				return "align-justify";
			default:
				return "align-left";
		}
	};

	extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(this.blockRenderMap);

	TextEditor = asField(({ fieldState, ...props }) => {
		const { placeholder, helperText, buttonOptions, isRequired } = this.props;
		const { value, error } = fieldState;
		const { minimizePlaceholder } = this.state;
		const isFocused = minimizePlaceholder ? " focus" : "";
		const hasError = error ? " has-error" : "";
		if (value && value.length > 0 && minimizePlaceholder === false) {
			this.setState({
				minimizePlaceholder: true,
			});
		}

		let urlInput;
		if (this.state.showURLInput) {
			urlInput = this.renderLinkInput();
		}

		let imageInput;
		if (this.state.showImageInput) {
			imageInput = this.renderImageInput();
		}

		const { editorState } = this.state;
		const selection = editorState.getSelection();

		const blockType = editorState
			.getCurrentContent()
			.getBlockForKey(selection.getStartKey())
			.getType();

		return (
			<Fragment>
				{imageInput}
				<div className={`text-editor-wrap${isFocused}${hasError}`}>
					<div className="input-box text-editor">
						<div className="placeholder caption-text">
							{isRequired && <span className="isrequired-label">*</span>}
							{placeholder}
						</div>
						<div className="input-tools">
							{(!buttonOptions || buttonOptions.includes("block")) && (
								<div className="text-editor-btn selector block-selector">
									<BlockSelector onChange={this.toggleBlockType} value={blockType} />
									<i className="fa fa-caret-down"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("ta-left")) && (
								<div className="text-editor-btn" onMouseDown={() => this.setAlignment("LEFT")}>
									<i className="fa fa-align-left"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("ta-center")) && (
								<div className="text-editor-btn" onMouseDown={() => this.setAlignment("CENTER")}>
									<i className="fa fa-align-center"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("ta-right")) && (
								<div className="text-editor-btn" onMouseDown={() => this.setAlignment("RIGHT")}>
									<i className="fa fa-align-right"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("ta-justify")) && (
								<div className="text-editor-btn" onMouseDown={() => this.setAlignment("JUSTIFY")}>
									<i className="fa fa-align-justify"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("b")) && (
								<div className="text-editor-btn" onMouseDown={() => this.setInlineStyle("BOLD")}>
									<i className="fa fa-bold"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("i")) && (
								<div className="text-editor-btn" onMouseDown={() => this.setInlineStyle("ITALIC")}>
									<i className="fa fa-italic"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("u")) && (
								<div className="text-editor-btn" onMouseDown={() => this.setInlineStyle("UNDERLINE")}>
									<i className="fa fa-underline"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("ol")) && (
								<div
									className="text-editor-btn"
									onMouseDown={() => this.toggleBlockType("ordered-list-item")}
								>
									<i className="fa fa-list-ol"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("ul")) && (
								<div
									className="text-editor-btn"
									onMouseDown={() => this.toggleBlockType("unordered-list-item")}
								>
									<i className="fa fa-list-ul"></i>
								</div>
							)}
							{(!buttonOptions || buttonOptions.includes("a")) && (
								<Fragment>
									<div className="text-editor-btn" onMouseDown={this.promptForLink}>
										<i className="fa fa-link"></i>
									</div>
									<div className="text-editor-btn" onMouseDown={this.removeLink}>
										<i className="fa fa-unlink"></i>
									</div>
								</Fragment>
							)}
							{(!buttonOptions || buttonOptions.includes("img")) && (
								<div className="text-editor-btn" onMouseDown={this.promptForImage}>
									<i className="fa fa-image"></i>
								</div>
							)}
						</div>
						{urlInput}
						<div className="editor">
							<Editor
								{...props}
								editorState={this.state.editorState}
								handleReturn={this.handleReturn}
								handleKeyCommand={this.handleKeyCommand}
								handleBeforeInput={this.handleBeforeInput}
								onTab={this.handleTab}
								onChange={this.onChange}
								ref={this.editor}
								blockRendererFn={this.blockRender}
								blockStyleFn={this.blockStyleFn}
								blockRenderMap={this.extendedBlockRenderMap}
							/>
						</div>
					</div>
					<div className="row">
						<div className="col-xs-6">
							<div className="caption-text helper-message">{helperText}</div>
						</div>
						<div className="col-xs-6">
							<div className="error-message caption-text">{error ? error : ""}</div>
						</div>
					</div>
				</div>
			</Fragment>
		);
	});

	removeImage = (event, blockKey) => {
		event.preventDefault();
		event.stopPropagation();
		let { editorState } = this.state;
		let content = editorState.getCurrentContent();
		const newSelection = new SelectionState({
			anchorKey: blockKey,
			anchorOffset: 0,
			focusKey: blockKey,
			focusOffset: 0,
		});

		const afterKey = content.getKeyAfter(blockKey);
		const afterBlock = content.getBlockForKey(afterKey);
		let targetRange;

		if (
			afterBlock &&
			afterBlock.getType() === "unstyled" &&
			afterBlock.getLength() === 0 &&
			afterBlock === content.getBlockMap().last()
		) {
			targetRange = new SelectionState({
				anchorKey: blockKey,
				anchorOffset: 0,
				focusKey: afterKey,
				focusOffset: 0,
			});
		} else {
			targetRange = new SelectionState({
				anchorKey: blockKey,
				anchorOffset: 0,
				focusKey: blockKey,
				focusOffset: 2,
			});
		}

		// change the blocktype and remove the characterList entry with the sticker
		content = Modifier.setBlockType(content, targetRange, "unstyled");
		content = Modifier.removeRange(content, targetRange, "backward");

		// force to new selection
		const newState = EditorState.push(editorState, content, "remove-image");
		this.setState({
			editorState: EditorState.forceSelection(newState, newSelection),
		});
	};

	Image = props => {
		let entityKey = props.block.getEntityAt(0);
		let i = 1;
		while (!entityKey && i < props.block.getLength()) {
			entityKey = props.block.getEntityAt(i);
			i++;
		}
		if (entityKey) {
			const entity = props.contentState.getEntity(entityKey);
			const { src } = entity.getData();

			return (
				<div className={`image-wrapper ${props.selection.focusKey}`}>
					<div
						className="image-remove"
						onClick={event => {
							this.removeImage(event, props.block.getKey());
						}}
					>
						<i className="fa fa-times-circle"></i>
					</div>
					<img style={{ maxWidth: "100%" }} src={src} alt="Verris" />
				</div>
			);
		}
		return null;
	};

	BLOCK_TYPE_HEADINGS = [
		{ label: "H1", style: "header-one" },
		{ label: "H2", style: "header-two" },
		{ label: "H3", style: "header-three" },
		{ label: "H4", style: "header-four" },
		{ label: "H5", style: "header-five" },
		{ label: "H6", style: "header-six" },
	];

	render() {
		const { field, id, autocomplete, type } = this.props;
		var inputProps = {
			field: field,
			id: id,
			...(autocomplete ? { autocomplete: autocomplete } : {}),
			...(type ? { type: type } : {}),
		};
		return (
			<this.TextEditor
				{...inputProps}
				onFocus={() => this.onFocus()}
				onBlur={evt => this.onBlur(evt.target.value)}
				validate={this.validate}
				validateOnChange
				validateOnBlur
			/>
		);
	}
}

TextInput.propTypes = {
	field: PropTypes.string,
	id: PropTypes.string,
	placeholder: PropTypes.string,
	autocomplete: PropTypes.string,
	isRequired: PropTypes.bool,
	minLength: PropTypes.number,
	maxLength: PropTypes.number,
	validationRegex: PropTypes.string,
	regexMessage: PropTypes.string,
	type: PropTypes.string,
	helperText: PropTypes.string,
	fieldApi: PropTypes.any,
	buttonOptions: PropTypes.object,
};

function findLinkEntities(contentBlock, callback, contentState) {
	contentBlock.findEntityRanges(character => {
		const entityKey = character.getEntity();
		return entityKey !== null && contentState.getEntity(entityKey).getType() === "LINK";
	}, callback);
}
const Link = props => {
	const { url } = props.contentState.getEntity(props.entityKey).getData();
	return <a href={url}>{props.children}</a>;
};

Link.propTypes = {
	children: PropTypes.any,
	contentState: PropTypes.object,
	entityKey: PropTypes.string,
};

const Wrapper = validatehoc(asField(({ fieldState, ...props }) => <TextInput fieldState={fieldState} {...props} />));

export default Wrapper;
